import { useState, useMemo } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  Plus,
  Filter,
  Eye,
  Copy,
  Check,
  ExternalLink,
  Search,
  X,
  Calendar,
  DollarSign,
  CreditCard,
} from "lucide-react";
import {
  Box,
  Card,
  Flex,
  Grid,
  Button,
  Text,
  Badge,
  IconButton,
  Link,
  TextField,
} from "@radix-ui/themes";
import PageTemplate from "@/components/page-template/PageTemplate";
import DataGrid, { DataGridColumn } from "@/components/ui/DataGrid";
import DropdownMenu from "@/components/ui/DropdownMenu";
import Modal from "@/components/ui/Modal";
import InputField from "@/components/form/InputField";
import {
  Payment,
  CreatePaymentRequest,
  PAYMENT_STATUSES,
  providerLabel,
  providerBadgeColor,
} from "@/types/breeze/AnythinkPay";
import {
  useAnythinkPayPayments,
  useAnythinkPayPaymentsByStatus,
  useCreateAnythinkPayPayment,
} from "@/hooks/useAnythinkPayPayments";
import withPermissionRequired from "@/hoc/withPermissionRequired";
import DynamicReferenceField from "@/components/form/DynamicReferenceField";
import { useAuth } from "@/providers/AuthProvider";
import toast from "react-hot-toast";
import { useQuery } from "@tanstack/react-query";
import { entityService } from "@/services/breeze/EntityService";
import { formatDate, formatRelativeDate } from "@/utils/date";

interface PaymentFormValues {
  amount: number;
  description: string;
  reference: string;
  customer_email: string;
}

const createPaymentSchema = Yup.object().shape({
  amount: Yup.number()
    .min(0.01, "Amount must be greater than 0")
    .required("Amount is required"),
  description: Yup.string().required("Description is required"),
  customer_email: Yup.string()
    .email("Invalid email format")
    .required("Customer email is required"),
  reference: Yup.string(),
});

// Helper component to display payable information
function PayableInfo({ payment }: { payment: Payment }) {
  const { currentTenant } = useAuth();

  const { data: entity } = useQuery({
    queryKey: ["entity-by-id", payment.payable_entity_id],
    queryFn: () => entityService.getById(payment.payable_entity_id!),
    enabled: !!payment.payable_entity_id,
  });

  if (!payment.payable_entity_id && !payment.payable_id) {
    return null;
  }

  const recordUrl =
    entity && payment.payable_id
      ? `/org/${currentTenant?.id}/entities/${entity.name}/${payment.payable_id}`
      : null;

  return (
    <Card>
      <Flex direction="column" gap="4" p="4">
        <Text size="3" weight="bold">
          Payable Information
        </Text>
        <Grid columns={{ initial: "1", md: "2" }} gap="4">
          {entity && (
            <Flex direction="column" gap="2">
              <Text size="2" weight="bold" color="gray">
                Entity
              </Text>
              <Text size="2">{entity.name}</Text>
            </Flex>
          )}
          {payment.payable_entity_id && !entity && (
            <Flex direction="column" gap="2">
              <Text size="2" weight="bold" color="gray">
                Entity ID
              </Text>
              <Text size="2">{payment.payable_entity_id}</Text>
            </Flex>
          )}
          {payment.payable_id && (
            <Flex direction="column" gap="2">
              <Text size="2" weight="bold" color="gray">
                Record
              </Text>
              {recordUrl ? (
                <Link
                  href={recordUrl}
                  target="_blank"
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  <Text size="2">#{payment.payable_id}</Text>
                  <ExternalLink size={14} />
                </Link>
              ) : (
                <Text size="2">#{payment.payable_id}</Text>
              )}
            </Flex>
          )}
        </Grid>
      </Flex>
    </Card>
  );
}

interface PaymentsPageProps {
  // "page" (default) renders the full Settings page chrome — title, description, rightContent toolbar.
  // "tab" renders the panel only, so it can be embedded as a tab inside another page (e.g. the
  // AnythinkPay hub's Payments tab) without producing a nested PageTemplate.
  chrome?: "page" | "tab";
  statusFilter?: string;
  providerFilter?: string;
  isCreateOpen?: boolean;
  onCreateOpenChange?: (open: boolean) => void;
}

function PaymentsPage({
  chrome = "page",
  statusFilter: controlledStatusFilter,
  providerFilter: controlledProviderFilter,
  isCreateOpen: controlledCreateOpen,
  onCreateOpenChange,
}: PaymentsPageProps) {
  const { currentTenant } = useAuth();
  const [internalStatusFilter, setInternalStatusFilter] = useState<string>("all");
  const [internalProviderFilter, setInternalProviderFilter] = useState<string>("all");
  const [internalCreateOpen, setInternalCreateOpen] = useState(false);
  const statusFilter = controlledStatusFilter ?? internalStatusFilter;
  const setStatusFilter = setInternalStatusFilter;
  const providerFilter = controlledProviderFilter ?? internalProviderFilter;
  const setProviderFilter = setInternalProviderFilter;
  const isCreateModalOpen = controlledCreateOpen ?? internalCreateOpen;
  const setIsCreateModalOpen = onCreateOpenChange ?? setInternalCreateOpen;
  const toolbarOwnedByParent = controlledStatusFilter !== undefined;
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(25);
  const [filter, setFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [copiedPaymentId, setCopiedPaymentId] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Fetch payments with pagination
  const {
    data: allPayments,
    isLoading: isLoadingAll,
    error: errorAll,
  } = useAnythinkPayPayments(currentPage, currentPageSize, filter);

  const {
    data: statusFilteredPayments,
    isLoading: isLoadingFiltered,
    error: errorFiltered,
  } = useAnythinkPayPaymentsByStatus(
    statusFilter,
    currentPage,
    currentPageSize,
    filter,
  );

  // Determine which data to show
  const payments = useMemo(() => {
    if (statusFilter === "all") {
      return allPayments;
    }
    return statusFilteredPayments;
  }, [statusFilter, allPayments, statusFilteredPayments]);

  const isLoading = statusFilter === "all" ? isLoadingAll : isLoadingFiltered;
  const error = statusFilter === "all" ? errorAll : errorFiltered;

  // Create payment mutation
  const createPayment = useCreateAnythinkPayPayment();

  const handleCreatePayment = (values: PaymentFormValues) => {
    // Ensure type and currency are set, and add redirect URLs from tenant settings
    const paymentData: CreatePaymentRequest = {
      ...values,
      type: "web",
      currency: "gbp",
      success_url:
        currentTenant?.tenant_settings?.payment_success_url ||
        `${window.location.origin}/org/${currentTenant?.id}/integrations/anythinkpay/payments/success`,
      cancel_url:
        currentTenant?.tenant_settings?.payment_cancel_url ||
        `${window.location.origin}/org/${currentTenant?.id}/integrations/anythinkpay/payments/cancelled`,
    };

    createPayment.mutate(paymentData, {
      onSuccess: (response) => {
        setIsCreateModalOpen(false);

        // If there's a checkout URL, open it
        if (response.checkout_url) {
          window.open(response.checkout_url, "_blank");
        }
      },
    });
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setCurrentPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
    // Note: Client-side filtering for now. Backend filtering can be added later.
  };

  // Client-side filtering for search
  // Note: For proper search, we'd need to fetch all payments or implement server-side search
  // For now, this filters the current page only
  const filteredPayments = useMemo(() => {
    if (!payments?.items) return payments;

    const query = searchQuery.trim().toLowerCase();
    const items = payments.items.filter((payment) => {
      // Legacy rows have no provider — treat them as Stripe (only Stripe wrote Payments before
      // this column existed).
      const provider = (payment.provider ?? "stripe").toLowerCase();
      if (providerFilter !== "all" && provider !== providerFilter) return false;
      if (!query) return true;
      return (
        payment.description?.toLowerCase().includes(query) ||
        payment.reference?.toLowerCase().includes(query) ||
        payment.id.toLowerCase().includes(query) ||
        payment.amount.toString().includes(query)
      );
    });

    if (items.length === payments.items.length && !query && providerFilter === "all") {
      return payments;
    }

    return {
      ...payments,
      items,
      total_items: items.length,
      total_pages: 1,
    };
  }, [payments, searchQuery, providerFilter]);

  const handleCopyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast.error("Failed to copy to clipboard");
    }
  };

  const handleOpenCheckoutUrl = (payment: Payment) => {
    if (payment.checkout_url && payment.status.toLowerCase() !== "completed") {
      window.open(payment.checkout_url, "_blank");
    } else {
      toast.error("Checkout URL not available for this payment");
    }
  };

  // Calculate summary statistics (use all payments, not filtered)
  const summaryStats = useMemo(() => {
    if (!payments?.items) {
      return {
        totalPayments: 0,
        totalAmount: 0,
        totalFees: 0,
        totalNetAmount: 0,
        completedPayments: 0,
        completedAmount: 0,
        completedFees: 0,
        completedNetAmount: 0,
        pendingPayments: 0,
      };
    }

    const stats = payments.items.reduce(
      (acc, payment) => {
        acc.totalPayments += 1;
        acc.totalAmount += payment.amount;

        if (payment.fee_amount != null) {
          acc.totalFees += payment.fee_amount;
        }
        if (payment.net_amount != null) {
          acc.totalNetAmount += payment.net_amount;
        }

        if (payment.status.toLowerCase() === "completed") {
          acc.completedPayments += 1;
          acc.completedAmount += payment.amount;
          if (payment.fee_amount != null) {
            acc.completedFees += payment.fee_amount;
          }
          if (payment.net_amount != null) {
            acc.completedNetAmount += payment.net_amount;
          }
        } else if (payment.status.toLowerCase() === "pending") {
          acc.pendingPayments += 1;
        }

        return acc;
      },
      {
        totalPayments: 0,
        totalAmount: 0,
        totalFees: 0,
        totalNetAmount: 0,
        completedPayments: 0,
        completedAmount: 0,
        completedFees: 0,
        completedNetAmount: 0,
        pendingPayments: 0,
      },
    );

    return stats;
  }, [payments]);

  // Use filtered payments for display — both search and provider filters apply.
  const displayPayments = filteredPayments;

  // Per-provider breakdown for the reconciliation card. Uses the unfiltered payment set so the
  // "money in by source" totals always reflect everything, even while the user has a filter on.
  const providerBreakdown = useMemo(() => {
    const groups: Record<string, { count: number; total: number }> = {};
    (payments?.items ?? []).forEach((p) => {
      if (p.status.toLowerCase() !== "completed") return;
      const key = (p.provider ?? "stripe").toLowerCase();
      groups[key] = groups[key] ?? { count: 0, total: 0 };
      groups[key].count += 1;
      groups[key].total += p.amount;
    });
    return groups;
  }, [payments]);


  const handleViewPayment = (payment: Payment) => {
    setSelectedPayment(payment);
    setCopiedPaymentId(null); // Reset copied state when viewing a new payment
  };

  const handleCopyPaymentLink = async (payment: Payment) => {
    if (!payment.checkout_url) {
      toast.error("No payment link available for this payment");
      return;
    }

    try {
      await navigator.clipboard.writeText(payment.checkout_url);
      setCopiedPaymentId(payment.id);
      toast.success("Payment link copied to clipboard!");

      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedPaymentId(null);
      }, 2000);
    } catch (err) {
      toast.error("Failed to copy payment link");
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "green";
      case "pending":
        return "blue";
      case "failed":
        return "red";
      case "cancelled":
        return "gray";
      case "refunded":
        return "orange";
      case "partiallyrefunded":
        return "yellow";
      default:
        return "gray";
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  // Define columns for DataGrid
  const columns: DataGridColumn<Payment>[] = [
    {
      field: "id",
      header: "Payment ID",
      width: 200,
      valueGetter: (payment) => payment.id.substring(0, 8) + "...",
    },
    {
      field: "description",
      header: "Description",
      flex: 2,
    },
    {
      field: "amount",
      header: "Amount",
      valueGetter: (payment) =>
        formatCurrency(payment.amount, payment.currency),
      width: 130,
    },
    {
      field: "status",
      header: "Status",
      cellRenderer: (payment) => (
        <Badge color={getStatusBadgeColor(payment.status)} size="2">
          {payment.status}
        </Badge>
      ),
      width: 130,
    },
    {
      field: "provider",
      header: "Source",
      cellRenderer: (payment) => (
        <Badge color={providerBadgeColor(payment.provider)} variant="soft">
          {providerLabel(payment.provider)}
        </Badge>
      ),
      width: 110,
    },
    {
      field: "reference",
      header: "Reference",
      valueGetter: (payment) => payment.reference || "—",
      width: 150,
    },
    {
      field: "created_at",
      header: "Created",
      valueGetter: (payment) => formatRelativeDate(payment.created_at),
      width: 120,
    },
    {
      field: "paid_at",
      header: "Paid",
      valueGetter: (payment) => formatRelativeDate(payment.paid_at),
      width: 120,
    },
  ];

  const toolbar = (
    <Flex gap="2">
      <DropdownMenu
        icon={
          <Button variant="outline" size="2">
            <Filter size={16} />
            Source:{" "}
            {providerFilter === "all" ? "All" : providerLabel(providerFilter)}
          </Button>
        }
        items={[
          { label: "All sources", onClick: () => setProviderFilter("all") },
          { label: "Stripe", onClick: () => setProviderFilter("stripe") },
          { label: "Apple", onClick: () => setProviderFilter("apple") },
          { label: "Google Play", onClick: () => setProviderFilter("google") },
        ]}
      />
      <DropdownMenu
        icon={
          <Button variant="outline" size="2">
            <Filter size={16} />
            Status:{" "}
            {statusFilter === "all"
              ? "All"
              : PAYMENT_STATUSES.find((s) => s.value === statusFilter)?.label ||
                statusFilter}
          </Button>
        }
        items={[
          {
            label: "All Payments",
            onClick: () => setStatusFilter("all"),
          },
          ...PAYMENT_STATUSES.map((status) => ({
            label: status.label,
            onClick: () => setStatusFilter(status.value),
          })),
        ]}
      />

      <Button onClick={() => setIsCreateModalOpen(true)}>
        <Plus size={16} />
        Create Payment
      </Button>
    </Flex>
  );

  if (error) {
    if (chrome === "tab") return <div>Error loading payments: {error.message}</div>;
    return (
      <PageTemplate
        title="AnythinkPay Payments"
        description="Manage payments processed through AnythinkPay"
      >
        <div>Error loading payments: {error.message}</div>
      </PageTemplate>
    );
  }

  const Wrapper = ({ children }: { children: React.ReactNode }) =>
    chrome === "tab" ? (
      <Flex direction="column" gap="4">
        {!toolbarOwnedByParent && <Flex justify="end">{toolbar}</Flex>}
        {children}
      </Flex>
    ) : (
      <PageTemplate
        title="AnythinkPay Payments"
        description="Manage payments processed through AnythinkPay integration"
        rightContent={toolbar}
      >
        {children}
      </PageTemplate>
    );

  return (
    <Wrapper>
      {/* Per-source reconciliation — totals by payment processor so the business owner can compare
          Stripe vs Apple (vs Google in future) at a glance. Counts and totals are over completed
          payments only. Apple amounts are the listed plan price; Apple's actual settlement (after
          their commission) is reported in App Store Connect, not via the API. */}
      {Object.keys(providerBreakdown).length > 0 && (
        <Card mb="4">
          <Flex direction="column" gap="3" p="4">
            <Flex align="center" gap="2">
              <DollarSign size={18} color="var(--gray-11)" />
              <Text size="2" weight="medium">
                Money in by source
              </Text>
              <Text size="1" color="gray">
                — completed payments
              </Text>
            </Flex>
            <Grid columns={{ initial: "1", sm: "3" }} gap="3">
              {Object.entries(providerBreakdown).map(([key, stats]) => (
                <Flex
                  key={key}
                  direction="column"
                  gap="1"
                  style={{ borderLeft: "2px solid var(--gray-5)", paddingLeft: 12 }}
                >
                  <Text size="2" color="gray">
                    {providerLabel(key)}
                  </Text>
                  <Text size="4" weight="bold">
                    {formatCurrency(
                      stats.total,
                      payments?.items?.find(
                        (p) => (p.provider ?? "stripe").toLowerCase() === key,
                      )?.currency ?? "GBP",
                    )}
                  </Text>
                  <Text size="1" color="gray">
                    {stats.count} payment{stats.count === 1 ? "" : "s"}
                  </Text>
                </Flex>
              ))}
            </Grid>
            {providerBreakdown.apple && (
              <Text size="1" color="gray">
                Apple totals are the listed plan price. Apple's actual
                settlement (minus their commission, plus tax/promo
                adjustments) is reported in App Store Connect.
              </Text>
            )}
          </Flex>
        </Card>
      )}

      {/* Summary Statistics */}
      <Grid columns={{ initial: "1", sm: "2", md: "4" }} gap="4" mb="4">
        <Card>
          <Flex direction="column" gap="2" p="4">
            <Flex align="center" gap="2">
              <CreditCard size={18} color="var(--gray-11)" />
              <Text size="2" color="gray">
                Total Payments
              </Text>
            </Flex>
            <Text size="5" weight="bold">
              {payments?.total_items ?? 0}
            </Text>
          </Flex>
        </Card>
        <Card>
          <Flex direction="column" gap="2" p="4">
            <Flex align="center" gap="2">
              <DollarSign size={18} color="var(--gray-11)" />
              <Text size="2" color="gray">
                Total Amount
              </Text>
            </Flex>
            <Text size="5" weight="bold">
              {formatCurrency(
                summaryStats.totalAmount,
                payments?.items?.[0]?.currency || "GBP",
              )}
            </Text>
            {summaryStats.totalNetAmount > 0 && (
              <Text size="1" color="gray">
                Net:{" "}
                {formatCurrency(
                  summaryStats.totalNetAmount,
                  payments?.items?.[0]?.currency || "GBP",
                )}
              </Text>
            )}
          </Flex>
        </Card>
        <Card>
          <Flex direction="column" gap="2" p="4">
            <Flex align="center" gap="2">
              <Check size={18} color="var(--green-11)" />
              <Text size="2" color="gray">
                Completed
              </Text>
            </Flex>
            <Text size="5" weight="bold">
              {summaryStats.completedPayments}
            </Text>
            <Text size="1" color="gray">
              {formatCurrency(
                summaryStats.completedAmount,
                payments?.items?.[0]?.currency || "GBP",
              )}
            </Text>
            {summaryStats.completedNetAmount > 0 && (
              <Text size="1" color="gray">
                Net:{" "}
                {formatCurrency(
                  summaryStats.completedNetAmount,
                  payments?.items?.[0]?.currency || "GBP",
                )}
              </Text>
            )}
          </Flex>
        </Card>
        <Card>
          <Flex direction="column" gap="2" p="4">
            <Flex align="center" gap="2">
              <Calendar size={18} color="var(--blue-11)" />
              <Text size="2" color="gray">
                Pending
              </Text>
            </Flex>
            <Text size="5" weight="bold">
              {summaryStats.pendingPayments}
            </Text>
          </Flex>
        </Card>
      </Grid>

      {/* Search Bar */}
      <Box mb="4">
        <Flex direction="column" gap="2">
          <TextField.Root
            size="2"
            placeholder="Search by description, reference, or payment ID..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          >
            <TextField.Slot>
              <Search size={16} />
            </TextField.Slot>
            {searchQuery && (
              <TextField.Slot>
                <IconButton
                  size="1"
                  variant="ghost"
                  onClick={() => handleSearchChange("")}
                >
                  <X size={14} />
                </IconButton>
              </TextField.Slot>
            )}
          </TextField.Root>
          {searchQuery.trim() && displayPayments?.items && (
            <Text size="1" color="gray">
              {displayPayments.items.length} result
              {displayPayments.items.length !== 1 ? "s" : ""} found
              {displayPayments.items.length < (payments?.items?.length || 0) &&
                " (searching current page only)"}
            </Text>
          )}
        </Flex>
      </Box>

      {searchQuery.trim() &&
        displayPayments?.items &&
        displayPayments.items.length === 0 && (
          <Card>
            <Flex direction="column" align="center" gap="3" p="6">
              <Text size="3" color="gray">
                No payments found matching "{searchQuery}"
              </Text>
              <Text size="2" color="gray">
                Try adjusting your search terms or clear the search to see all
                payments.
              </Text>
            </Flex>
          </Card>
        )}

      <DataGrid
        data={displayPayments as any}
        columns={columns}
        storageKey="anythinkpay_payments"
        isLoading={isLoading}
        currentPage={searchQuery.trim() ? 1 : currentPage}
        currentPageSize={currentPageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onFilterChange={handleFilterChange}
        rowSelection="none"
        showPagination={!searchQuery.trim()}
        onCellClicked={(event, payment) => {
          // Make rows clickable to view details
          if (event.colDef.field !== "actions") {
            handleViewPayment(payment);
          }
        }}
        actions={(payment: Payment) => (
          <Flex gap="1">
            <IconButton
              variant="ghost"
              size="1"
              onClick={(e) => {
                e.stopPropagation();
                handleViewPayment(payment);
              }}
              title="View details"
            >
              <Eye size={16} />
            </IconButton>
            {payment.checkout_url &&
              payment.status.toLowerCase() !== "completed" && (
                <IconButton
                  variant="ghost"
                  size="1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenCheckoutUrl(payment);
                  }}
                  title="Open checkout URL"
                >
                  <ExternalLink size={16} />
                </IconButton>
              )}
            <IconButton
              variant="ghost"
              size="1"
              onClick={(e) => {
                e.stopPropagation();
                handleCopyToClipboard(payment.id, `id-${payment.id}`);
              }}
              title="Copy payment ID"
            >
              {copiedField === `id-${payment.id}` ? (
                <Check size={16} />
              ) : (
                <Copy size={16} />
              )}
            </IconButton>
          </Flex>
        )}
      />

      {/* Create Payment Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create Payment"
        description="Create a new GBP web payment via Stripe Checkout"
        size="lg"
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsCreateModalOpen(false)}
              disabled={createPayment.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createPayment.isPending}
              form="create-payment-form"
            >
              {createPayment.isPending ? "Creating..." : "Create Payment"}
            </Button>
          </>
        }
      >
        <Formik
          initialValues={{
            amount: 0,
            description: "",
            reference: "",
            customer_email: "",
          }}
          validationSchema={createPaymentSchema}
          onSubmit={handleCreatePayment}
        >
          <Form id="create-payment-form">
            <Flex direction="column" gap="5" p="4">
              <Text size="3" weight="bold">
                Payment Details
              </Text>
              {/* Payment Info */}
              <Box>
                <Flex direction="column" gap="4">
                  <Text size="2" color="gray">
                    Type: Web | Currency: GBP (£)
                  </Text>
                </Flex>
              </Box>

              <Box>
                <Flex direction="column" gap="4">
                  <Text size="4" weight="bold">
                    Payable Information
                  </Text>
                  <DynamicReferenceField
                    entityFieldName="payable_entity_id"
                    recordFieldName="payable_id"
                    label="Payable"
                    description="Select the entity and record for the payment."
                  />
                </Flex>
              </Box>

              {/* Amount & Description */}
              <Box>
                <InputField
                  name="amount"
                  label="Amount (£)"
                  type="number"
                  required
                  options={{ placeholder: "10.00" }}
                  description="Amount in GBP (e.g., 10 for £10.00)"
                />

                <Box mt="4">
                  <InputField
                    name="description"
                    label="Description"
                    type="text"
                    required
                    options={{ placeholder: "Payment for..." }}
                  />
                </Box>
              </Box>
              <Text size="3" weight="bold">
                Customer Information
              </Text>
              {/* Customer Information */}
              <Box>
                <Grid columns={{ initial: "1", md: "2" }} gap="4">
                  <InputField
                    name="reference"
                    label="Reference (Optional)"
                    type="text"
                    options={{ placeholder: "Internal reference" }}
                  />

                  <InputField
                    name="customer_email"
                    label="Customer Email"
                    type="email"
                    required
                    options={{ placeholder: "customer@example.com" }}
                  />
                </Grid>
              </Box>

              {!currentTenant?.tenant_settings?.payment_success_url && (
                <Box>
                  <Text size="2" color="gray">
                    Note: Using default payment redirect pages. You can
                    configure custom URLs in Organisation Settings for a
                    personalised experience.
                  </Text>
                </Box>
              )}
            </Flex>
          </Form>
        </Formik>
      </Modal>

      {/* Payment Details Modal */}
      {selectedPayment && (
        <Modal
          isOpen={!!selectedPayment}
          onClose={() => setSelectedPayment(null)}
          title={`Payment Details`}
          description={`Payment ID: ${selectedPayment.id}`}
          size="lg"
          actions={
            <Flex gap="2">
              <Button
                variant="outline"
                onClick={() =>
                  handleCopyToClipboard(selectedPayment.id, "modal-id")
                }
              >
                {copiedField === "modal-id" ? (
                  <>
                    <Check size={16} />
                    Copied ID
                  </>
                ) : (
                  <>
                    <Copy size={16} />
                    Copy ID
                  </>
                )}
              </Button>
              {selectedPayment.checkout_url &&
                selectedPayment.status.toLowerCase() !== "completed" && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => handleOpenCheckoutUrl(selectedPayment)}
                    >
                      <ExternalLink size={16} />
                      Open Checkout
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleCopyPaymentLink(selectedPayment)}
                    >
                      {copiedPaymentId === selectedPayment.id ? (
                        <>
                          <Check size={16} />
                          Copied Link
                        </>
                      ) : (
                        <>
                          <Copy size={16} />
                          Copy Link
                        </>
                      )}
                    </Button>
                  </>
                )}
              <Button onClick={() => setSelectedPayment(null)}>Close</Button>
            </Flex>
          }
        >
          <Flex direction="column" gap="4" p="4">
            {/* Header Cards */}
            <Grid columns={{ initial: "1", md: "2" }} gap="4">
              <Card>
                <Flex direction="column" gap="3" p="4">
                  <Text size="2" weight="bold" color="gray">
                    Amount
                  </Text>
                  <Text size="5" weight="bold">
                    {formatCurrency(
                      selectedPayment.amount,
                      selectedPayment.currency,
                    )}
                  </Text>
                  {selectedPayment.fee_amount != null && (
                    <Text size="1" color="gray">
                      Fee:{" "}
                      {formatCurrency(
                        selectedPayment.fee_amount,
                        selectedPayment.currency,
                      )}
                    </Text>
                  )}
                  {selectedPayment.net_amount != null && (
                    <Text size="1" color="gray">
                      Net:{" "}
                      {formatCurrency(
                        selectedPayment.net_amount,
                        selectedPayment.currency,
                      )}
                    </Text>
                  )}
                </Flex>
              </Card>

              <Card>
                <Flex direction="column" gap="3" p="4">
                  <Text size="2" weight="bold" color="gray">
                    Status
                  </Text>
                  <Badge
                    color={getStatusBadgeColor(selectedPayment.status)}
                    size="3"
                  >
                    {selectedPayment.status}
                  </Badge>
                </Flex>
              </Card>
            </Grid>

            {/* Fee Breakdown - Only show if fee information is available */}
            {(selectedPayment.fee_amount != null ||
              selectedPayment.net_amount != null) && (
              <Card>
                <Flex direction="column" gap="4" p="4">
                  <Text size="3" weight="bold">
                    Fee Breakdown
                  </Text>
                  <Grid columns={{ initial: "1", md: "3" }} gap="4">
                    <Flex direction="column" gap="2">
                      <Text size="2" weight="bold" color="gray">
                        Gross Amount
                      </Text>
                      <Text size="3" weight="bold">
                        {formatCurrency(
                          selectedPayment.amount,
                          selectedPayment.currency,
                        )}
                      </Text>
                    </Flex>
                    {selectedPayment.fee_amount != null && (
                      <Flex direction="column" gap="2">
                        <Text size="2" weight="bold" color="gray">
                          Processing Fee
                        </Text>
                        <Text size="3" weight="bold" color="red">
                          -
                          {formatCurrency(
                            selectedPayment.fee_amount,
                            selectedPayment.currency,
                          )}
                        </Text>
                      </Flex>
                    )}
                    {selectedPayment.net_amount != null && (
                      <Flex direction="column" gap="2">
                        <Text size="2" weight="bold" color="gray">
                          Net Amount
                        </Text>
                        <Text size="3" weight="bold" color="green">
                          {formatCurrency(
                            selectedPayment.net_amount,
                            selectedPayment.currency,
                          )}
                        </Text>
                      </Flex>
                    )}
                  </Grid>
                </Flex>
              </Card>
            )}

            {/* Payment Information */}
            <Card>
              <Flex direction="column" gap="4" p="4">
                <Text size="3" weight="bold">
                  Payment Information
                </Text>
                <Grid columns={{ initial: "1", md: "2" }} gap="4">
                  <Flex direction="column" gap="2">
                    <Text size="2" weight="bold" color="gray">
                      Payment ID
                    </Text>
                    <Flex align="center" gap="2">
                      <Text
                        size="2"
                        style={{
                          fontFamily: "monospace",
                          userSelect: "text",
                        }}
                      >
                        {selectedPayment.id}
                      </Text>
                      <IconButton
                        size="1"
                        variant="ghost"
                        onClick={() =>
                          handleCopyToClipboard(
                            selectedPayment.id,
                            "modal-id-inline",
                          )
                        }
                      >
                        {copiedField === "modal-id-inline" ? (
                          <Check size={14} />
                        ) : (
                          <Copy size={14} />
                        )}
                      </IconButton>
                    </Flex>
                  </Flex>
                  <Flex direction="column" gap="2">
                    <Text size="2" weight="bold" color="gray">
                      Type
                    </Text>
                    <Text size="2" style={{ textTransform: "capitalize" }}>
                      {selectedPayment.type}
                    </Text>
                  </Flex>
                  <Flex direction="column" gap="2">
                    <Text size="2" weight="bold" color="gray">
                      Description
                    </Text>
                    <Text size="2">{selectedPayment.description || "—"}</Text>
                  </Flex>
                  <Flex direction="column" gap="2">
                    <Text size="2" weight="bold" color="gray">
                      Reference
                    </Text>
                    <Text size="2">{selectedPayment.reference || "—"}</Text>
                  </Flex>
                  <Flex direction="column" gap="2">
                    <Text size="2" weight="bold" color="gray">
                      Currency
                    </Text>
                    <Text size="2">
                      {selectedPayment.currency.toUpperCase()}
                    </Text>
                  </Flex>
                </Grid>
              </Flex>
            </Card>

            {/* Timestamps */}
            <Card>
              <Flex direction="column" gap="4" p="4">
                <Text size="3" weight="bold">
                  Timeline
                </Text>
                <Grid columns={{ initial: "1", md: "2" }} gap="4">
                  <Flex direction="column" gap="2">
                    <Text size="2" weight="bold" color="gray">
                      Created At
                    </Text>
                    <Text size="2">
                      {formatDate(selectedPayment.created_at)}
                    </Text>
                  </Flex>
                  {selectedPayment.paid_at && (
                    <Flex direction="column" gap="2">
                      <Text size="2" weight="bold" color="gray">
                        Paid At
                      </Text>
                      <Text size="2">
                        {formatDate(selectedPayment.paid_at)}
                      </Text>
                    </Flex>
                  )}
                  <Flex direction="column" gap="2">
                    <Text size="2" weight="bold" color="gray">
                      Last Updated
                    </Text>
                    <Text size="2">
                      {formatDate(selectedPayment.updated_at)}
                    </Text>
                  </Flex>
                </Grid>
              </Flex>
            </Card>

            {/* Payable Information */}
            <PayableInfo payment={selectedPayment} />

            {/* Checkout Information */}
            {selectedPayment.checkout_url && (
              <Card>
                <Flex direction="column" gap="3" p="4">
                  <Flex justify="between" align="center">
                    <Text size="3" weight="bold">
                      Checkout Information
                    </Text>
                    {selectedPayment.status.toLowerCase() !== "completed" && (
                      <Button
                        size="2"
                        variant="outline"
                        onClick={() => handleOpenCheckoutUrl(selectedPayment)}
                      >
                        <ExternalLink size={14} />
                        Open
                      </Button>
                    )}
                  </Flex>
                  <Flex direction="column" gap="2">
                    <Text size="2" weight="bold" color="gray">
                      Checkout URL
                    </Text>
                    {selectedPayment.status.toLowerCase() === "completed" ? (
                      <Text
                        size="2"
                        color="gray"
                        style={{ fontStyle: "italic" }}
                      >
                        Payment completed - checkout URL no longer active
                      </Text>
                    ) : (
                      <Flex align="center" gap="2">
                        <Text
                          size="2"
                          style={{
                            fontFamily: "monospace",
                            wordBreak: "break-all",
                            userSelect: "text",
                            flex: 1,
                          }}
                        >
                          {selectedPayment.checkout_url}
                        </Text>
                        <IconButton
                          size="1"
                          variant="ghost"
                          onClick={() =>
                            handleCopyToClipboard(
                              selectedPayment.checkout_url!,
                              "checkout-url",
                            )
                          }
                        >
                          {copiedField === "checkout-url" ? (
                            <Check size={14} />
                          ) : (
                            <Copy size={14} />
                          )}
                        </IconButton>
                      </Flex>
                    )}
                  </Flex>
                </Flex>
              </Card>
            )}
          </Flex>
        </Modal>
      )}
    </Wrapper>
  );
}

export default withPermissionRequired("anythink_payments:read", PaymentsPage);
